import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useInvoicingApiActionSegmentEvents } from '@features/invoices/hooks/useInvoicingApiActionSegmentEvents'
import { PaymentMethodsTrackingOptions } from '@features/invoices/utils/getPaymentMethodsTrackingOptions'

import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { approveInvoice } from '../../../query-api'

export interface ApproveInvoiceDraftOptions {
  onError?: () => void
  onSuccess?: (approvedInvoiceId: number) => void
  paymentMethodsTrackingOptions?: PaymentMethodsTrackingOptions
}

export const useApproveInvoiceDraft = () => {
  const { t } = useTranslation()
  const { sendEvent } = useInvoicingApiActionSegmentEvents()
  const { mutate: approveInvoiceFn, isLoading: isProcessing } = useMutation(approveInvoice)

  const approveInvoiceDraft = useCallback(
    (
      invoiceDraftId: number,
      documentType: InvoiceDocumentType,
      { onError, onSuccess, paymentMethodsTrackingOptions }: ApproveInvoiceDraftOptions,
    ) => {
      approveInvoiceFn(
        { invoiceDraftId, documentType },
        {
          onError: () => {
            notify({
              id: NotificationKeys.ExternalInvoiceApproved,
              message: t('error_saving'),
              variant: 'error',
            })
            onError?.()
          },
          onSuccess: (approvedInvoiceResponse) => {
            const data = approvedInvoiceResponse.data
            // Invoice approval's response is an array, while credit note approval's response is an object
            const approvedInvoiceId = Array.isArray(data) ? data[0]?.id : data?.id

            sendEvent.document.approved(documentType, approvedInvoiceId, paymentMethodsTrackingOptions)
            onSuccess?.(approvedInvoiceId)
          },
        },
      )
    },
    [approveInvoiceFn, sendEvent.document, t],
  )

  return { approveInvoiceDraft, isProcessing }
}
